import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import QRCode from 'qrcode';
import { post } from '../../lib/api.ts';
import { useMe, useRefreshMe } from '../../lib/auth.tsx';
import { AuthShell, AuthButton, Field, inputCls, btnCls, ErrorText, DONE_MS, wait } from '../../components/AuthShell.tsx';

/** 조직 진입 2단계 (CR-067: 그 조직 소속의 MFA): 인증 앱(TOTP) 코드·복구코드 또는 이메일 코드 */
export function MfaVerifyPage() {
  const nav = useNavigate();
  const me = useMe();
  const refresh = useRefreshMe();
  const methods = me.data?.mfaMethods ?? ['totp'];
  const [mode, setMode] = useState<'totp' | 'recovery' | 'email' | null>(null);
  const cur = mode ?? (methods.includes('totp') ? 'totp' : 'email');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  async function send() { setError(null); try { await post('/auth/mfa/email/send'); setSent(true); } catch (err) { setError(err); } }
  async function submit(e: FormEvent) {
    e.preventDefault(); setError(null); setBusy(true);
    try {
      await post('/auth/mfa/verify', cur === 'recovery' ? { recoveryCode: code } : cur === 'email' ? { emailCode: code } : { code });
      setBusy(false); setDone(true);
      await Promise.all([refresh(), wait(DONE_MS)]);
      nav('/', { replace: true });
    } catch (err) { setError(err); setBusy(false); }
  }
  const sw = (m: 'totp' | 'recovery' | 'email') => { setMode(m); setCode(''); setError(null); };
  return (
    <AuthShell title="2단계 인증" subtitle={cur === 'email' ? `${me.data?.email ?? '계정 이메일'} 로 받은 코드를 입력하세요.` : '인증 앱에 표시된 코드를 입력하세요.'} lockTenant footer={<button className="hover:underline" onClick={() => post('/account/logout').catch(() => post('/auth/logout')).then(() => { refresh(); nav('/login'); })}>다른 계정으로 로그인</button>}>
      {cur === 'email' && <button type="button" className={btnCls + ' mb-3 bg-white! text-slate-700! border border-slate-300 hover:bg-slate-50!'} onClick={send}>{sent ? '코드 다시 보내기' : '이메일로 코드 보내기'}</button>}
      <form onSubmit={submit}>
        <Field label={cur === 'recovery' ? '복구코드' : cur === 'email' ? '이메일의 6자리 코드' : '인증 앱의 6자리 코드'}>
          <input className={inputCls + ' font-mono tracking-widest'} inputMode={cur === 'recovery' ? 'text' : 'numeric'} autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
        </Field>
        <AuthButton busy={busy} done={done} busyText="확인 중…" doneText="확인됨">확인</AuthButton>
        <ErrorText error={error} />
      </form>
      <div className="mt-3 flex gap-3 text-[12px] text-accent-strong">
        {methods.includes('totp') && cur !== 'totp' && <button type="button" className="hover:underline" onClick={() => sw('totp')}>인증 앱 코드로 입력</button>}
        {methods.includes('totp') && cur !== 'recovery' && <button type="button" className="hover:underline" onClick={() => sw('recovery')}>복구코드로 입력</button>}
        {methods.includes('email') && cur !== 'email' && <button type="button" className="hover:underline" onClick={() => sw('email')}>이메일 코드로 받기</button>}
      </div>
    </AuthShell>
  );
}

/** MFA 등록: QR → 코드 확인 → 복구코드 표시 */
export function MfaEnrollPage() {
  const nav = useNavigate();
  const me = useMe();
  const refresh = useRefreshMe();
  const [enroll, setEnroll] = useState<{ secret: string; otpauth: string } | null>(null);
  const [qr, setQr] = useState<string>('');
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  // CR-067: 조직·역할이 이메일 수단을 허용하면 먼저 수단을 고른다 (관리자 역할·break-glass 는 인증 앱만)
  const emailAllowed = !!me.data?.mfaAllowed?.includes('email');
  const [method, setMethod] = useState<'totp' | 'email' | null>(null);
  const cur = method ?? (me.data ? (emailAllowed ? null : 'totp') : null);
  const [emailSent, setEmailSent] = useState(false);
  useEffect(() => {
    if (cur !== 'totp' || enroll) return;
    post<{ secret: string; otpauth: string }>('/auth/mfa/enroll').then(async (r) => { setEnroll(r); setQr(await QRCode.toDataURL(r.otpauth, { width: 200, margin: 1 })); }).catch(setError);
  }, [cur, enroll]);
  async function sendEmail() { setError(null); try { await post('/auth/mfa/email/enroll'); setEmailSent(true); } catch (err) { setError(err); } }
  async function confirmEmail(e: FormEvent) {
    e.preventDefault(); setError(null); setBusy(true);
    try { await post('/auth/mfa/email/enroll/confirm', { code }); await refresh(); nav('/', { replace: true }); } catch (err) { setError(err); } finally { setBusy(false); }
  }

  async function confirm(e: FormEvent) {
    e.preventDefault(); setError(null); setBusy(true);
    try { const r = await post<{ recoveryCodes: string[] }>('/auth/mfa/enroll/confirm', { code }); setCodes(r.recoveryCodes); await refresh(); } catch (err) { setError(err); } finally { setBusy(false); }
  }

  if (codes) {
    return (
      <AuthShell title="복구코드를 보관하세요" lockTenant>
        <p className="text-sm text-slate-600 mb-3">인증 앱을 쓸 수 없을 때 한 번씩 사용할 수 있습니다. 지금만 표시됩니다.</p>
        <div className="grid grid-cols-2 gap-1 font-mono text-sm bg-slate-50 border border-slate-200 p-3">{codes.map((c) => <div key={c}>{c}</div>)}</div>
        <button className={btnCls + ' mt-4'} onClick={() => nav('/', { replace: true })}>보관했습니다. 시작하기</button>
      </AuthShell>
    );
  }
  const pendingNote = me.data?.mfaPending ? '이 조직의 정책상 2단계 인증이 필요합니다. 조직마다 따로 등록합니다' : undefined;
  if (cur === null) {
    return (
      <AuthShell title="2단계 인증 등록" subtitle="이 조직에 들어갈 때 쓸 인증 수단을 고르세요." lockTenant footer={pendingNote}>
        <button type="button" className={btnCls} onClick={() => setMethod('totp')}>인증 앱 (권장)</button>
        <button type="button" className={btnCls + ' mt-2 bg-white! text-slate-700! border border-slate-300 hover:bg-slate-50!'} onClick={() => { setMethod('email'); void sendEmail(); }}>이메일 코드</button>
        <p className="mt-3 text-[11.5px] text-slate-500 leading-relaxed">인증 앱은 Google Authenticator 등으로 6자리 코드를 만듭니다. 이메일 코드는 들어갈 때마다 계정 이메일로 코드를 받습니다.</p>
        <ErrorText error={error} />
      </AuthShell>
    );
  }
  if (cur === 'email') {
    return (
      <AuthShell title="이메일 인증 등록" subtitle={`${me.data?.email ?? '계정 이메일'} 로 보낸 코드를 입력하세요.`} lockTenant footer={pendingNote}>
        <form onSubmit={confirmEmail}>
          <Field label="이메일의 6자리 코드"><input className={inputCls + ' font-mono tracking-widest'} inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} required autoFocus /></Field>
          <AuthButton busy={busy} busyText="확인 중…">등록</AuthButton>
          <ErrorText error={error} />
        </form>
        <div className="mt-3 flex gap-3 text-[12px] text-accent-strong">
          <button type="button" className="hover:underline" onClick={sendEmail}>{emailSent ? '코드 다시 보내기' : '코드 보내기'}</button>
          <button type="button" className="hover:underline" onClick={() => { setMethod('totp'); setCode(''); setError(null); }}>인증 앱으로 등록</button>
        </div>
      </AuthShell>
    );
  }
  return (
    <AuthShell title="2단계 인증 등록" subtitle="계정을 보호하기 위해 인증 앱을 등록합니다." lockTenant footer={pendingNote}>
      {!enroll ? <div className="text-sm">준비 중…<ErrorText error={error} /></div> : (
        <form onSubmit={confirm}>
          <p className="text-sm text-slate-600">Google Authenticator, Microsoft Authenticator 등 인증 앱으로 QR 을 스캔하세요.</p>
          {qr && <img src={qr} alt="QR" className="mx-auto my-3 border border-slate-200" width={200} height={200} />}
          <div className="text-xs text-slate-500 mb-3">수동 입력 키: <span className="font-mono break-all">{enroll.secret}</span></div>
          <Field label="앱에 표시된 6자리 코드"><input className={inputCls + ' font-mono tracking-widest'} inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} required autoFocus /></Field>
          <AuthButton busy={busy} busyText="확인 중…">등록</AuthButton>
          <ErrorText error={error} />
        </form>
      )}
    </AuthShell>
  );
}
